import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useDeferredValue, useEffect, useState } from "react";
import { _localization } from "../../config/location";
import { ActionIcon, Button, Flex, Text, Title, Tooltip } from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { modals } from "@mantine/modals";
import DeleteFAQ from "./DeleteFAQ";

const DetailLineFAQs = ({
  dataDetail,
  onSuccess,
}: {
  dataDetail: any[];
  onSuccess: any;
}) => {
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const rowSelectionDeferred =
    useDeferredValue<MRT_RowSelectionState>(rowSelection);
  const [selectIds, setSelectIds] = useState<string[]>([]);

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "question",
        header: "FAQ",
        Cell: ({ renderedCellValue }) => (
          <Tooltip label={renderedCellValue} withArrow multiline w={500}>
            <Text fw={400} size="16px" truncate="end" w={500}>
              {renderedCellValue}
            </Text>
          </Tooltip>
        ),
      },
      {
        accessorKey: "answer",
        header: "Câu trả lời",
        Cell: ({ renderedCellValue }) => (
          <Tooltip label={renderedCellValue} withArrow multiline w={500}>
            <Text fw={400} size="16px" truncate="end" w={500}>
              {renderedCellValue}
            </Text>
          </Tooltip>
        ),
      },
      {
        accessorKey: "createdDate",
        header: "Ngày tạo",
        Cell: ({ row }) => (
          <Text size="sm">
            {row.original.createdDate
              ? new Date(row.original.createdDate).toLocaleDateString("vi-VN", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : ""}
          </Text>
        ),
      },
      {
        accessorKey: "lastUpdatedDate",
        header: "Ngày cập nhật",
        Cell: ({ row }) => (
          <Text size="sm">
            {row.original.lastUpdatedDate
              ? new Date(row.original.lastUpdatedDate).toLocaleDateString(
                  "vi-VN",
                  {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  }
                )
              : ""}
          </Text>
        ),
      },
      {
        accessorKey: "action",
        header: "Thao tác",
        size: 60,
        Cell: ({ row }) => (
          <Flex justify={"space-between"} gap={"xs"}>
            <ActionIcon
              variant="light"
              color="red"
              onClick={() => modalsDeleteFAQ([Number(row.original.id)])}
            >
              <IconTrash size={16} stroke={1.5} />
            </ActionIcon>
          </Flex>
        ),
      },
    ],
    [dataDetail]
  );

  const modalsDeleteFAQ = (listId: number[]) => {
    modals.openConfirmModal({
      title: <Title order={5}>Xóa FAQ</Title>,
      size: "auto",
      children: <DeleteFAQ listId={listId} onSuccess={onSuccess} />,
      confirmProps: { display: "none" },
      cancelProps: { display: "none" },
    });
  };

  useEffect(() => {
    //   startTransition(() => {
    const valuesList = Object.keys(rowSelectionDeferred);
    setSelectIds(valuesList);
    if (valuesList.length < 1) setSelectIds([]);
    else {
      const valuesList = Object.keys(rowSelectionDeferred);
      setSelectIds(valuesList);
    }
    //  });
  }, [rowSelectionDeferred]);

  const table = useMantineReactTable({
    columns,
    data: dataDetail,
    enableColumnResizing: false,
    positionToolbarAlertBanner: "bottom",
    enableRowSelection: true,
    //add custom action buttons to top-left of top toolbar
    enableTopToolbar: false,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    getRowId: (row) => row.id?.toString(),
    initialState: {
      showColumnFilters: false,
      columnVisibility: { id: true, phone: false, fax: false },
      columnPinning: {
        left: [
          "mrt-row-select",
          "question",
          "answer",
          "createdDate",
          "lastUpdatedDate",
          "action",
        ],
      },
      density: "xs",
    },
    renderBottomToolbarCustomActions: ({ table }) => (
      <>
        <Button
          leftSection={<IconTrash size={16} />}
          color="red"
          disabled={selectIds.length === 0}
          onClick={() => modalsDeleteFAQ(selectIds.map((item) => Number(item)))}
        >
          Xóa FAQs
        </Button>
      </>
    ),
    mantineTableContainerProps: {
      style: {
        height: "600px",
      },
    },
    enableStickyHeader: true,
    manualPagination: true,
    onRowSelectionChange: setRowSelection,
    rowCount: rowCount,
    onPaginationChange: setPagination,
    mantineTableBodyCellProps: ({ row }) => ({
      style: {
        fontWeight: "500", //conditional styling
        fontSize: "12.5px",
        padding: "7.5px 15px",
      },
    }),
    state: {
      isLoading,
      pagination: pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading, //loading for the first time with no data
      rowSelection,
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    columnFilterDisplayMode: "popover",
    mantineTableBodyRowProps: ({ row }) => ({
      style: { cursor: "pointer" },
    }),
  });

  return (
    <div style={{ paddingTop: 10, paddingBottom: 10 }}>
      <MantineReactTable table={table} />
    </div>
  );
};

export default DetailLineFAQs;
